import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { videoPath, timeToSeconds } from '../data/videos';
import '../styles/pages/work.css';

/**
 * Click-to-load YouTube card. The iframe is only mounted once the visitor hits
 * play, so YouTube sets no cookies and loads no player JS on page view — the
 * poster is the only third-party request. Uses youtube-nocookie.com.
 *
 * `showDetails` adds the highlights list, chapter jumps and footnote — the
 * video page turns it on, the /demos card grid leaves it off. In that mode the
 * body splits into a reading column and a chapters/share aside.
 * `showTitle={false}` drops the card heading when the page hero already
 * carries the title as the h1. `featured` gives the wide, side-by-side layout
 * used for the lead card in a grid.
 */
function VideoCard({
  video,
  onPlay,
  showPermalink = true,
  showDetails = false,
  showTitle = true,
  featured = false,
  headingLevel: Heading = 'h3'
}) {
  const [playing, setPlaying] = useState(false);
  const [startAt, setStartAt] = useState(0);
  const [posterQuality, setPosterQuality] = useState('maxresdefault');
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const descRef = useRef(null);

  // In the card grid the description is clamped to three lines. Only offer the
  // toggle when there is actually more to read — a two-line description
  // shouldn't sprout a "Read more" that reveals nothing. Re-measured on resize
  // because the clamp height and font size both change at breakpoints.
  const measure = useCallback(() => {
    const el = descRef.current;
    if (!el) return;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, []);

  useEffect(() => {
    if (showDetails || expanded) return undefined;
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, showDetails, expanded]);

  const poster = `https://i.ytimg.com/vi/${video.youtubeId}/${posterQuality}.jpg`;
  const watchUrl = `https://www.youtube.com/watch?v=${video.youtubeId}`;
  const embedUrl =
    `https://www.youtube-nocookie.com/embed/${video.youtubeId}` +
    `?autoplay=1&rel=0&modestbranding=1${startAt ? `&start=${startAt}` : ''}`;

  const handlePlay = (seconds = 0) => {
    setStartAt(seconds);
    setPlaying(true);
    if (typeof onPlay === 'function') onPlay(video, seconds);
  };

  // Jumping to a chapter while the player is already mounted needs a remount,
  // because `start` is only read when the iframe loads. Keying off startAt
  // would be enough, but React reuses the node when only the src changes.
  const handleChapter = (chapter) => {
    const seconds = timeToSeconds(chapter.t);
    setPlaying(false);
    // Next tick, so the iframe unmounts before it comes back with a new start.
    setTimeout(() => handlePlay(seconds), 0);
  };

  // Copies the Moneli permalink, not the YouTube URL, so shared links survive a
  // re-upload. Clipboard API needs a secure context; fall back to selecting the
  // URL in a prompt so the visitor can still copy it by hand.
  const handleCopy = async () => {
    const url = `${window.location.origin}${videoPath(video)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this link:', url);
    }
  };

  const cardClass = [
    'demo-card',
    'video-card',
    'work-vcard',
    showDetails ? 'work-vcard--page' : '',
    featured ? 'work-vcard--featured' : ''
  ].filter(Boolean).join(' ');

  const title = showTitle && (
    <Heading className="demo-card__title work-vcard__title">
      {showPermalink
        ? <Link className="video-card__title-link" to={videoPath(video)}>{video.title}</Link>
        : video.title}
    </Heading>
  );

  const description = (
    <div
      ref={descRef}
      id={`video-desc-${video.slug}`}
      className={
        'video-card__desc' + (!showDetails && !expanded ? ' video-card__desc--clamped' : '')
      }
    >
      {video.paragraphs.map((paragraph, i) => (
        <p className="demo-card__desc" key={i}>{paragraph}</p>
      ))}
    </div>
  );

  const actions = (
    <div className="video-card__actions">
      {showPermalink && (
        <Link className="video-card__link" to={videoPath(video)}>
          Video page <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
        </Link>
      )}
      <button type="button" className="video-card__link video-card__copy" onClick={handleCopy}>
        <i className={copied ? 'fa-solid fa-check' : 'fa-solid fa-link'} aria-hidden="true"></i>
        {copied ? 'Link copied' : 'Copy share link'}
      </button>
      <a
        className="video-card__link"
        href={watchUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        YouTube <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
      </a>
    </div>
  );

  return (
    <div className={cardClass}>
      <div className="video-card__frame">
        {playing ? (
          <iframe
            className="video-card__player"
            src={embedUrl}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            className="video-card__poster"
            onClick={() => handlePlay(0)}
            aria-label={`Play video: ${video.title}`}
            data-cursor="Play"
          >
            <img
              src={poster}
              alt=""
              loading={showDetails ? 'eager' : 'lazy'}
              /* Not every upload has a maxres thumbnail; fall back once to hq. */
              onError={() => {
                if (posterQuality === 'maxresdefault') setPosterQuality('hqdefault');
              }}
            />
            <span className="video-card__play" aria-hidden="true">
              <i className="fa-solid fa-play"></i>
            </span>
            {video.duration && (
              <span className="video-card__duration" aria-hidden="true">
                <i className="fa-regular fa-clock"></i> {video.duration}
              </span>
            )}
          </button>
        )}
      </div>

      {showDetails ? (
        <div className="demo-card__body work-vcard__body">
          <div className="work-vcard__main">
            {title}
            {description}

            {video.highlights && (
              <div className="video-card__section">
                <h2 className="video-card__section-title">{video.highlights.title}</h2>
                <ul className="video-card__list">
                  {video.highlights.items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            )}

            {video.callout && (
              <p className="video-card__callout">{video.callout}</p>
            )}

            {video.disclaimer && (
              <p className="video-card__disclaimer">
                <i className="fa-solid fa-circle-info" aria-hidden="true"></i>
                <span>{video.disclaimer}</span>
              </p>
            )}

            {video.footnote && (
              <p className="video-card__footnote">{video.footnote}</p>
            )}
          </div>

          <aside className="work-vcard__aside" aria-label="Chapters and sharing">
            {video.chapters && (
              <div className="video-card__section">
                <h2 className="video-card__section-title">Chapters</h2>
                <ol className="video-card__chapters">
                  {video.chapters.map((chapter) => (
                    <li key={chapter.t}>
                      <button
                        type="button"
                        className="video-card__chapter"
                        onClick={() => handleChapter(chapter)}
                      >
                        <span className="video-card__chapter-time">{chapter.t}</span>
                        <span>{chapter.label}</span>
                      </button>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {actions}
          </aside>
        </div>
      ) : (
        <div className="demo-card__body work-vcard__body">
          {video.duration && (
            <p className="work-vcard__kicker">Walkthrough <span aria-hidden="true">·</span> {video.duration}</p>
          )}
          {title}
          {description}

          {(overflows || expanded) && (
            <button
              type="button"
              className="video-card__more"
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
              aria-controls={`video-desc-${video.slug}`}
            >
              {expanded ? 'Show less' : 'Read more'}
              <i className={`fa-solid fa-chevron-${expanded ? 'up' : 'down'}`} aria-hidden="true"></i>
            </button>
          )}

          {actions}
        </div>
      )}
    </div>
  );
}

export default VideoCard;
